import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { formatDate } from "@/lib/date";
import { toast } from "sonner";
import { DatabaseBackup, Download, Loader2, Plus } from "lucide-react";

function formatSize(b) {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

function formatTime(iso) {
  const d = new Date(iso);
  return d.toLocaleTimeString("ro-RO", { hour: "2-digit", minute: "2-digit", hour12: false });
}

function BackupRow({ b, onDownload, busy }) {
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-slate-50 transition-colors" data-testid={`backup-row-${b.id}`}>
      <div className="min-w-0">
        <div className="text-sm font-medium tabular">
          {formatDate(b.created_at)} <span className="text-slate-400">· {formatTime(b.created_at)}</span>
        </div>
        <div className="text-xs text-slate-500 flex flex-wrap gap-x-2">
          <span>{formatSize(b.size)}</span>
          <span>· {b.total_records} înregistrări</span>
          <span className={`text-[10px] uppercase tracking-wider px-1.5 rounded ${b.trigger === "manual" ? "bg-blue-50 text-blue-700" : "bg-slate-100 text-slate-600"}`}>
            {b.trigger === "manual" ? "Manual" : "Automat"}
          </span>
        </div>
      </div>
      <button
        onClick={() => onDownload(b)}
        disabled={busy}
        data-testid={`backup-download-${b.id}`}
        className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border border-slate-200 hover:border-slate-400 transition-colors disabled:opacity-50"
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Descarcă
      </button>
    </div>
  );
}

export default function Backups() {
  const [items, setItems] = useState(null);
  const [creating, setCreating] = useState(false);
  const [downloading, setDownloading] = useState(null);

  async function load() {
    const { data } = await api.get("/admin/backups");
    setItems(data);
  }
  useEffect(() => { load(); }, []);

  async function createNow() {
    setCreating(true);
    try {
      await api.post("/admin/backups");
      toast.success("Backup creat");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Backup eșuat");
    } finally { setCreating(false); }
  }

  async function download(b) {
    setDownloading(b.id);
    try {
      const res = await api.get(`/admin/backups/${b.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `novoauto-backup-${b.created_at.slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      toast.error("Descărcarea a eșuat");
    } finally { setDownloading(null); }
  }

  return (
    <div className="sm:pl-52 space-y-4" data-testid="backups-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Siguranța datelor</div>
          <h1 className="font-display text-3xl tracking-tight font-medium">Backup-uri</h1>
          <p className="text-sm text-slate-500 mt-1">Backup automat zilnic la 03:00 (ora României). Se păstrează ultimele 30.</p>
        </div>
        <button
          onClick={createNow}
          disabled={creating}
          data-testid="backup-now-btn"
          className="inline-flex items-center gap-1.5 text-sm font-medium px-4 py-2 rounded-full bg-[#0A0F1C] text-white hover:bg-slate-800 transition-colors disabled:opacity-60"
        >
          {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Backup acum
        </button>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100" data-testid="backups-list">
        {items === null && <div className="p-6 text-sm text-slate-400">Se încarcă…</div>}
        {items?.length === 0 && (
          <div className="p-10 text-center" data-testid="backups-empty">
            <DatabaseBackup className="w-10 h-10 mx-auto text-slate-300" />
            <div className="mt-3 font-display text-lg">Niciun backup încă</div>
            <div className="text-sm text-slate-500 mt-1">Primul backup automat va rula la noapte, sau apasă „Backup acum”.</div>
          </div>
        )}
        {items?.map((b) => <BackupRow key={b.id} b={b} onDownload={download} busy={downloading === b.id} />)}
      </div>
      <p className="text-xs text-slate-400">Restaurare: descarcă fișierul și folosește „Import date (restore)” din meniu.</p>
    </div>
  );
}
